import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useNotify } from 'ra-core';

import { ContractorOwnerEditDialog } from '@/components/ContractorOwnerFormDialog';
import { AssignablePerson, ContractorOwner, RelatedPerson } from '@/types';
import {
    createRelatedPerson,
    fetchProjectRelatedPeople,
    searchAssignablePeople,
    setProjectRelatedPeople,
    updateRelatedPerson,
} from '@/utils/related_people_utils';
import {
    matchContractorOwnerByEmail,
    searchContractorOwners,
} from '@/utils/contractor_owner_utils';

type QuoteRelatedPeopleTabProps = {
    projectId: number | null;
};

function assignableOptionLabel(person: AssignablePerson): string {
    const email = (person.email || '').trim();
    const suffix =
        person.source === 'contractor_owner' && !email
            ? '(no email)'
            : email
              ? `<${email}>`
              : '';
    return suffix ? `${person.name} ${suffix}` : person.name;
}

function assignableToContractorOwner(person: AssignablePerson): ContractorOwner {
    return {
        id: person.id,
        name: person.name,
        company_name: person.company_name || '',
        email: person.email || '',
        phone_number: person.phone_number || '',
    };
}

function contractorOwnerToAssignable(updated: ContractorOwner): AssignablePerson {
    return {
        id: Number(updated.id),
        name: updated.name,
        email: (updated.email || '').trim() || null,
        source: 'contractor_owner',
        company_name: updated.company_name,
        phone_number: updated.phone_number,
    };
}

export const QuoteRelatedPeopleTab = ({ projectId }: QuoteRelatedPeopleTabProps) => {
    const notify = useNotify();
    const [people, setPeople] = useState<RelatedPerson[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [assignOptions, setAssignOptions] = useState<AssignablePerson[]>([]);
    const [assignInput, setAssignInput] = useState('');
    const [assignLoading, setAssignLoading] = useState(false);
    const [selectedAssign, setSelectedAssign] = useState<AssignablePerson | null>(null);
    const [assignEditOpen, setAssignEditOpen] = useState(false);

    const [linkedEditMode, setLinkedEditMode] = useState<'contractor' | 'related' | null>(
        null,
    );
    const [linkedPersonEdit, setLinkedPersonEdit] = useState<RelatedPerson | null>(null);
    const [linkedContractorEdit, setLinkedContractorEdit] = useState<ContractorOwner | null>(
        null,
    );
    const [linkedEditForm, setLinkedEditForm] = useState({ name: '', email: '' });
    const [linkedEditSaving, setLinkedEditSaving] = useState(false);
    const [linkedEditLoading, setLinkedEditLoading] = useState(false);

    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [creating, setCreating] = useState(false);

    const reload = useCallback(async () => {
        if (projectId == null) {
            setPeople([]);
            return;
        }
        setLoading(true);
        try {
            setPeople(await fetchProjectRelatedPeople(projectId));
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Failed to load related people', {
                type: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [projectId, notify]);

    useEffect(() => {
        void reload();
    }, [reload]);

    useEffect(() => {
        let cancelled = false;
        setAssignLoading(true);
        searchAssignablePeople(assignInput)
            .then(opts => {
                if (!cancelled) setAssignOptions(opts);
            })
            .catch(() => {
                if (!cancelled) setAssignOptions([]);
            })
            .finally(() => {
                if (!cancelled) setAssignLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [assignInput]);

    const linkedIds = useMemo(
        () => new Set(people.map(p => Number(p.id))),
        [people],
    );
    const linkedEmails = useMemo(
        () => new Set(people.map(p => p.email.trim().toLowerCase())),
        [people],
    );

    const filteredAssignOptions = useMemo(
        () =>
            assignOptions.filter(o => {
                if (o.source === 'related_person' && linkedIds.has(Number(o.id))) {
                    return false;
                }
                const email = (o.email || '').trim().toLowerCase();
                if (o.source === 'contractor_owner' && email && linkedEmails.has(email)) {
                    return false;
                }
                return true;
            }),
        [assignOptions, linkedIds, linkedEmails],
    );

    const selectedHasEmail = Boolean((selectedAssign?.email || '').trim());
    const showAssignEdit =
        selectedAssign?.source === 'contractor_owner' && Number(selectedAssign.id) > 0;
    const showEditContractor = showAssignEdit && !selectedHasEmail;

    const persistLinks = async (payload: {
        relatedPersonIds: number[];
        contractorOwnerIds?: number[];
    }) => {
        if (projectId == null) return;
        setSaving(true);
        try {
            setPeople(
                await setProjectRelatedPeople(projectId, {
                    relatedPersonIds: payload.relatedPersonIds,
                    contractorOwnerIds: payload.contractorOwnerIds ?? [],
                }),
            );
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Failed to update related people', {
                type: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleAssign = async () => {
        if (!selectedAssign || !selectedHasEmail) return;

        const existingIds = people.map(p => Number(p.id));

        if (selectedAssign.source === 'related_person') {
            if (linkedIds.has(Number(selectedAssign.id))) return;
            await persistLinks({
                relatedPersonIds: [...existingIds, Number(selectedAssign.id)],
            });
        } else {
            await persistLinks({
                relatedPersonIds: existingIds,
                contractorOwnerIds: [Number(selectedAssign.id)],
            });
        }

        setSelectedAssign(null);
        setAssignInput('');
    };

    const handleUnlink = async (personId: number) => {
        await persistLinks({
            relatedPersonIds: people.map(p => Number(p.id)).filter(id => id !== personId),
        });
    };

    const closeLinkedEdit = () => {
        if (linkedEditSaving) return;
        setLinkedEditMode(null);
        setLinkedPersonEdit(null);
        setLinkedContractorEdit(null);
        setLinkedEditForm({ name: '', email: '' });
    };

    const openLinkedEdit = async (person: RelatedPerson) => {
        setLinkedEditLoading(true);
        setLinkedPersonEdit(person);
        setLinkedEditForm({ name: person.name, email: person.email });
        try {
            const matches = await searchContractorOwners(person.email);
            const contractor = matchContractorOwnerByEmail(matches, person.email);
            if (contractor) {
                setLinkedContractorEdit(contractor);
                setLinkedEditMode('contractor');
            } else {
                setLinkedContractorEdit(null);
                setLinkedEditMode('related');
            }
        } catch {
            setLinkedContractorEdit(null);
            setLinkedEditMode('related');
        } finally {
            setLinkedEditLoading(false);
        }
    };

    const handleSaveLinkedRelatedEdit = async () => {
        if (linkedPersonEdit == null) return;
        const name = linkedEditForm.name.trim();
        const email = linkedEditForm.email.trim();
        if (!name || !email) {
            notify('Name and email are required', { type: 'warning' });
            return;
        }
        setLinkedEditSaving(true);
        try {
            const updated = await updateRelatedPerson(Number(linkedPersonEdit.id), {
                name,
                email,
            });
            setPeople(prev =>
                prev.map(p => (Number(p.id) === Number(updated.id) ? updated : p)),
            );
            closeLinkedEdit();
            notify('Related person updated', { type: 'success' });
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Failed to update related person', {
                type: 'error',
            });
        } finally {
            setLinkedEditSaving(false);
        }
    };

    const handleCreate = async () => {
        const name = newName.trim();
        const email = newEmail.trim();
        if (!name || !email) {
            notify('Name and email are required', { type: 'warning' });
            return;
        }
        if (projectId == null) return;

        setCreating(true);
        try {
            const created = await createRelatedPerson({ name, email });
            await persistLinks({
                relatedPersonIds: [...people.map(p => Number(p.id)), Number(created.id)],
            });
            setCreateOpen(false);
            setNewName('');
            setNewEmail('');
            notify('Related person created', { type: 'success' });
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Failed to create related person', {
                type: 'error',
            });
        } finally {
            setCreating(false);
        }
    };

    if (projectId == null) {
        return (
            <Box p={2}>
                <Typography color="text.secondary">
                    Link a project first to manage related people.
                </Typography>
            </Box>
        );
    }

    return (
        <Box p={2}>
            <Stack spacing={2}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'flex-start' }}
                >
                    <Autocomplete
                        sx={{ flex: 1, minWidth: 220 }}
                        options={filteredAssignOptions}
                        loading={assignLoading}
                        value={selectedAssign}
                        inputValue={assignInput}
                        onInputChange={(_, v) => setAssignInput(v)}
                        onChange={(_, v) => setSelectedAssign(v)}
                        getOptionLabel={assignableOptionLabel}
                        isOptionEqualToValue={(a, b) =>
                            a.source === b.source && Number(a.id) === Number(b.id)
                        }
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Assign existing person"
                                size="small"
                                helperText={
                                    showEditContractor
                                        ? 'Add an email before assigning'
                                        : undefined
                                }
                                FormHelperTextProps={{
                                    sx: showEditContractor
                                        ? { color: 'warning.main', fontSize: 11 }
                                        : undefined,
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {assignLoading ? (
                                                <CircularProgress color="inherit" size={16} />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    {showAssignEdit ? (
                        <Tooltip title="Edit contractor/owner">
                            <span>
                                <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => setAssignEditOpen(true)}
                                    disabled={saving}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Edit
                                </Button>
                            </span>
                        </Tooltip>
                    ) : null}
                    <Button
                        variant="outlined"
                        onClick={() => void handleAssign()}
                        disabled={!selectedAssign || !selectedHasEmail || saving}
                        sx={{ textTransform: 'none' }}
                    >
                        Assign
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setCreateOpen(true)}
                        disabled={saving}
                        sx={{ textTransform: 'none' }}
                    >
                        Create new
                    </Button>
                </Stack>

                {loading ? (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress size={24} />
                    </Box>
                ) : people.length === 0 ? (
                    <Typography color="text.secondary">
                        No related people linked yet.
                    </Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell align="right" width={96} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {people.map(person => (
                                <TableRow key={person.id}>
                                    <TableCell>{person.name}</TableCell>
                                    <TableCell>{person.email}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Edit person">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Edit ${person.name}`}
                                                    onClick={() => void openLinkedEdit(person)}
                                                    disabled={saving || linkedEditLoading}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Remove from project">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Unlink ${person.name}`}
                                                    onClick={() =>
                                                        void handleUnlink(Number(person.id))
                                                    }
                                                    disabled={saving}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Stack>

            <ContractorOwnerEditDialog
                open={assignEditOpen}
                contractor={
                    selectedAssign?.source === 'contractor_owner'
                        ? assignableToContractorOwner(selectedAssign)
                        : null
                }
                label="contractor/owner"
                onClose={() => setAssignEditOpen(false)}
                onUpdated={updated => {
                    setSelectedAssign(contractorOwnerToAssignable(updated));
                    setAssignEditOpen(false);
                    notify('Contractor/owner updated', { type: 'success' });
                }}
            />

            <ContractorOwnerEditDialog
                open={linkedEditMode === 'contractor'}
                contractor={linkedContractorEdit}
                label="contractor/owner"
                onClose={closeLinkedEdit}
                onUpdated={async updated => {
                    if (linkedPersonEdit == null) return;
                    const email = (updated.email || '').trim();
                    if (!email) {
                        notify('Email is required for related people', { type: 'warning' });
                        return;
                    }
                    try {
                        const synced = await updateRelatedPerson(Number(linkedPersonEdit.id), {
                            name: updated.name,
                            email,
                        });
                        setPeople(prev =>
                            prev.map(p =>
                                Number(p.id) === Number(synced.id) ? synced : p,
                            ),
                        );
                        closeLinkedEdit();
                        notify('Contractor/owner updated', { type: 'success' });
                    } catch (e) {
                        notify(
                            e instanceof Error
                                ? e.message
                                : 'Failed to sync related person',
                            { type: 'error' },
                        );
                    }
                }}
            />

            <Dialog
                open={linkedEditMode === 'related'}
                onClose={closeLinkedEdit}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Edit related person</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        <TextField
                            label="Name"
                            value={linkedEditForm.name}
                            onChange={e =>
                                setLinkedEditForm(f => ({ ...f, name: e.target.value }))
                            }
                            fullWidth
                            autoFocus
                            disabled={linkedEditSaving}
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={linkedEditForm.email}
                            onChange={e =>
                                setLinkedEditForm(f => ({ ...f, email: e.target.value }))
                            }
                            fullWidth
                            disabled={linkedEditSaving}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeLinkedEdit} disabled={linkedEditSaving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => void handleSaveLinkedRelatedEdit()}
                        disabled={linkedEditSaving}
                    >
                        {linkedEditSaving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)}>
                <DialogTitle>Create related person</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1} sx={{ minWidth: 320 }}>
                        <TextField
                            label="Name"
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            fullWidth
                            autoFocus
                        />
                        <TextField
                            label="Email"
                            value={newEmail}
                            onChange={e => setNewEmail(e.target.value)}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateOpen(false)} disabled={creating}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => void handleCreate()}
                        disabled={creating}
                    >
                        {creating ? 'Creating…' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
